import React from "react"
import { graphql, Link } from "gatsby"
import Image from "gatsby-image"
import ReactMarkdown from "react-markdown"
import Layout from "../components/Layout"
import SEO from "../components/SEO"

const ProjectTemplate = ({ data }) => {
  const {
    title,
    description,
    caseStudy,
    gallery,
    date_start,
    date_end,
    hours,
    client,
    outcomes,
    tech_stack,
    stack_icons,
    links,
    tags,
    image,
  } = data.project

  const dateLabel = date_start
    ? `${date_start}${date_end ? ` – ${date_end}` : " – Present"}`
    : null
  const github = links && links.find(l => l.label === "GitHub")
  const live = links && links.find(l => l.label === "Live")
  const techNames =
    stack_icons && stack_icons.length > 0
      ? stack_icons.map(s => s.name)
      : tech_stack || []
  const hasTech = techNames.length > 0

  return (
    <Layout>
      <SEO title={`${title} | Nishant Tiwari`} description={description} />
      <section className="project-page">
        <div className="project-wrap">
          <Link to="/projects" className="project-back-link">
            ← all projects
          </Link>

          <header className="project-head">
            {tags && tags.length > 0 && (
              <p className="project-eyebrow">{tags.slice(0, 4).join(" · ")}</p>
            )}
            <h1>{title}</h1>
            {description && <p className="project-lead">{description}</p>}
          </header>

          {image && image.childImageSharp && (
            <Image fluid={image.childImageSharp.fluid} className="project-hero-img" />
          )}

          <div className="project-layout">
            <aside className="project-rail">
              <dl className="project-facts">
                {dateLabel && (
                  <div className="project-fact">
                    <dt>Timeline</dt>
                    <dd>{dateLabel}</dd>
                  </div>
                )}
                {client && (
                  <div className="project-fact">
                    <dt>Client</dt>
                    <dd>{client}</dd>
                  </div>
                )}
                {hours ? (
                  <div className="project-fact">
                    <dt>Effort</dt>
                    <dd>{hours} hrs</dd>
                  </div>
                ) : null}
              </dl>

              {hasTech && (
                <div className="project-rail-block">
                  <h4 className="project-rail-h">Stack</h4>
                  <div className="project-rail-chips">
                    {techNames.map((n, i) => (
                      <span key={i} className="project-chip">
                        {n}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(github || live) && (
                <div className="project-rail-block project-rail-links">
                  {live && (
                    <a href={live.url} className="btn" target="_blank" rel="noopener noreferrer">
                      view live
                    </a>
                  )}
                  {github && (
                    <a href={github.url} className="btn" target="_blank" rel="noopener noreferrer">
                      github
                    </a>
                  )}
                </div>
              )}
            </aside>

            <div className="project-main">
              {caseStudy && (
                <article className="project-body">
                  <ReactMarkdown source={caseStudy} />
                </article>
              )}

              {gallery && gallery.length > 0 && (
                <section className="project-gallery">
                  <h2>Gallery</h2>
                  <div className="project-gallery-grid">
                    {gallery.map((src, i) => (
                      <a
                        href={src}
                        key={i}
                        className="project-gallery-item"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img src={src} alt={`${title} screenshot ${i + 1}`} loading="lazy" />
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {outcomes && outcomes.length > 0 && (
                <section className="project-outcomes">
                  <h2>Outcomes</h2>
                  <ul>
                    {outcomes.map((o, i) => (
                      <li key={i}>{o}</li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </div>

          <Link to="/projects" className="btn center-btn">
            back to projects
          </Link>
        </div>
      </section>
    </Layout>
  )
}

export const query = graphql`
  query GetSingleProject($slug: String) {
    project(slug: { eq: $slug }) {
      title
      slug
      description
      caseStudy
      gallery
      date_start
      date_end
      hours
      client
      tags
      outcomes
      tech_stack
      links {
        label
        url
      }
      image {
        childImageSharp {
          fluid {
            ...GatsbyImageSharpFluid_withWebp_tracedSVG
          }
        }
      }
      stack_icons {
        name
      }
    }
  }
`

export default ProjectTemplate
